<script setup>
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore.js'
import { userApi } from '@/api/User/UserApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import InputText from '@/components/Forms/InputText.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { Format as FormatUtils } from '@/models/utils/FormatUtils.js'
import Validation from '@/models/utils/Validation.js'
import ValidationError from '@/components/Forms/InputError.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'

const toaster = new Toaster()

/**
 * Validation object to add/remove validation errors
 * @type {[Validation] extends [Ref] ? IfAny<Validation, Ref<Validation>, Validation> : Ref<UnwrapRef<Validation>, UnwrapRef<Validation> | Validation>}
 */
const validation = ref(new Validation())

const tenant = ref(null)
const tenantSettings = ref({})
const isSaving = ref(false)
const isLoading = ref(false)
const showDiscardChangesModal = ref(false)
const selectedDisableOption = ref('disable_days')
const lastLoginDays = ref(120)
const warningDays = ref(7)
const reEnabledGraceDays = ref(7)
const passwordExpirationEnabled = ref(false)
const passwordExpirationDays = ref(90)
const isReadOnly = computed(() => {
	return selectedDisableOption.value === 'disable_never'
})
const nonEmptyNumberFilter = (event, callback) => {
	if (event.target.value === '') {
		event.target.value = '0'
		callback(event.target.value)
	}
}
const beforeEditNumber = (event, callback) => {
	if (event.target.value === '0' && event.data) {
		event.target.value = ''
		callback(event.target.value)
	}
}

const emit = defineEmits(['dismiss'])

onMounted(() => {
	loadSettings()
})

function loadSettings()
{
	isLoading.value = true

	userApi.getTenantById(useAuthStore().getAuth.user().tenant_id, (tenantData) => {
		tenant.value = tenantData;
		tenantSettings.value = JSON.parse(tenant.value.settings_json);

		selectedDisableOption.value = tenantSettings.value.inactive_accounts.disable_enabled.value ? 'disable_days' : 'disable_never'
		lastLoginDays.value = tenantSettings.value.inactive_accounts.last_login_days.value
		warningDays.value = tenantSettings.value.inactive_accounts.warning_days.value
		reEnabledGraceDays.value = tenantSettings.value.inactive_accounts.reenabled_grace_days.value
		passwordExpirationDays.value = tenantSettings.value.passwords.expiration_days.value
		passwordExpirationEnabled.value = tenantSettings.value.passwords.expiration_enabled.value

		isLoading.value = false;
	})
}

/**
 * Save settings.
 * Note, SanitizeInput middleware will strip out html/script tags.
 */
function save() {
	if (!validate()) return

	const settings = {
		inactive_accounts: {
			disable_enabled: selectedDisableOption.value === 'disable_days',
			last_login_days: lastLoginDays.value,
			warning_days: warningDays.value,
			reenabled_grace_days: reEnabledGraceDays.value,
		}
	}

	isSaving.value = true
	userApi.updateTenantSettings({ settings: settings },
		() => {
			isSaving.value = false
			toaster.success('Inactive accounts settings successfully updated!')

			emit('dismiss', true)
		}, (e) => {
			validation.value.errors = e.showErrorBanner()
			isSaving.value = false
		})
}

function validate()
{
	if (selectedDisableOption.value === 'disable_never') return true

	const lastLoginDaysMinimum = passwordExpirationEnabled.value ? passwordExpirationDays.value : tenantSettings.value.inactive_accounts.last_login_days.minimum
	const warningDaysMinimum = tenantSettings.value.inactive_accounts.warning_days.minimum
	const reEnabledGraceDaysMinimum = tenantSettings.value.inactive_accounts.reenabled_grace_days.minimum

	const toCompare = [
		{ current: Number.parseInt(lastLoginDays.value), minimum: lastLoginDaysMinimum, compareType: 'less_equal', fieldName: 'last_login_days', message: `Last log in days is required and must be greater than ${lastLoginDaysMinimum}.`},
		{ current: Number.parseInt(warningDays.value), minimum: warningDaysMinimum, compareType: 'less', fieldName: 'warning_days', message: `Warning email days is required and must be at least ${warningDaysMinimum}.`},
		{ current: Number.parseInt(reEnabledGraceDays.value), minimum: reEnabledGraceDaysMinimum, compareType: 'less', fieldName: 'reenabled_grace_days', message: `Re-enable grace days is required and must be at least ${reEnabledGraceDaysMinimum}.`},
		{ current: Number.parseInt(lastLoginDays.value), minimum: warningDays.value, compareType: 'less', fieldName: 'warning_days', message: `Warning days must be less than last login days ${lastLoginDays.value}.`},
	]

	validation.value.removeAll()
	toCompare.forEach((setting) => {
		if (!setting.current || (
			(setting.compareType === 'less' && parseInt(setting.current) < setting.minimum) ||
			(setting.compareType === 'less_equal' && parseInt(setting.current) <= setting.minimum))
		)
		{
			validation.value.add('settings.inactive_accounts.' + setting.fieldName, setting.message)
		}
	})

	return validation.value.count() === 0;
}

function changesMade()
{
	const toCompare = [
		{ current: selectedDisableOption.value, previous: tenantSettings.value.inactive_accounts.disable_enabled.value ? 'disable_days' : 'disable_never' },
		{ current: Number.parseInt(lastLoginDays.value), previous: Number.parseInt(tenantSettings.value.inactive_accounts.last_login_days.value) },
		{ current: Number.parseInt(warningDays.value), previous: Number.parseInt(tenantSettings.value.inactive_accounts.warning_days.value) },
		{ current: Number.parseInt(reEnabledGraceDays.value), previous: Number.parseInt(tenantSettings.value.inactive_accounts.reenabled_grace_days.value) },
	]

	if (toCompare.some((setting) => setting.current !== setting.previous))
	{
		showDiscardChangesModal.value = true
	}
	else
	{
		emit('dismiss', false)
	}
}
</script>

<template>
	<LoadingSpinner v-if="isLoading" />
	<div v-else>
		<div class="flex flex-col mb-6">
			<span class="text-xl font-semibold text-heading">Inactive Accounts</span>
			<span class="text-sm font-medium mb-5 text-heading">Disable accounts that haven’t logged in for a set number of days. Works with or without password expiration.</span>

			<div class="mb-5 flex flex-col space-y-2 text-sm text-caption">
				<span class="font-medium text-sm text-grayscale-body">Auto-disable</span>
				<label class="inline-flex cursor-pointer items-center gap-1">
					<input type="radio" value="disable_never" v-model="selectedDisableOption" class="peer sr-only" />
					<!-- Unchecked -->
					<img src="@/assets/images/components/radio-outline.svg" class="peer-checked:hidden" alt="Never disable inactive accounts" />

					<!-- Checked -->
					<img src="@/assets/images/components/radio-selected.svg" class="hidden peer-checked:block" alt="Never disable inactive accounts" />
					<span>Never disable inactive accounts</span>
				</label>

				<label class="inline-flex cursor-pointer items-center gap-1">
					<input type="radio" value="disable_days" v-model="selectedDisableOption" class="peer sr-only" />
					<!-- Unchecked -->
					<img src="@/assets/images/components/radio-outline.svg" class="peer-checked:hidden" alt="Disable accounts after a period of inactivity" />

					<!-- Checked -->
					<img src="@/assets/images/components/radio-selected.svg" class="hidden peer-checked:block" alt="Disable accounts after a period of inactivity" />
					<span>Disable accounts after a period of inactivity</span>
				</label>
			</div>

			<div class="flex flex-col px-3 border-l-2 border-l-primary-subtle" :inert="isReadOnly">
				<span class="font-medium text-sm text-grayscale-body">Disable when the last log in is more than</span>
				<div class="flex space-x-2 mt-2">
					<InputText class="w-fit !my-0" type="number" v-model="lastLoginDays" :disabled="isReadOnly" input-width="!w-24" input-height="!h-9.5"
					           :input-filter="(event) => nonEmptyNumberFilter(event, (val) => lastLoginDays = val)"
					           :before-input="(event) => beforeEditNumber(event, (val) => lastLoginDays = val)" />
					<span class="h-fit my-auto text-sm text-caption">{{ FormatUtils.pluralize('day', lastLoginDays) }} ago</span>
				</div>
				<span v-if="passwordExpirationEnabled" class="h-fit my-auto text-xs text-caption mt-2">Must be more than the password expiration period ({{passwordExpirationDays}} {{FormatUtils.pluralize('day', passwordExpirationDays)}}).</span>
				<ValidationError :errors="validation.errors" field="settings.inactive_accounts.last_login_days" class="mt-2 ml-1" />

				<span class="font-medium text-sm text-grayscale-body mt-5">Send a warning email</span>
				<div class="flex space-x-2 mt-2">
					<InputText class="w-fit !my-0" type="number" v-model="warningDays" :disabled="isReadOnly" input-width="!w-24" input-height="!h-9.5"
					           :input-filter="(event) => nonEmptyNumberFilter(event, (val) => warningDays = val)"
					           :before-input="(event) => beforeEditNumber(event, (val) => warningDays = val)" />
					<span class="h-fit my-auto text-sm text-caption">{{ FormatUtils.pluralize('day', warningDays) }} before an account is disabled</span>
				</div>
				<span class="h-fit my-auto text-xs text-caption mt-2">Logging in before an account is disabled resets the inactivity count.</span>
				<ValidationError :errors="validation.errors" field="settings.inactive_accounts.warning_days" class="mt-2 ml-1" />

				<span class="font-medium text-sm text-grayscale-body mt-5">After an admin re-enables an account</span>
				<div class="flex space-x-2 mt-2">
					<span class="h-fit my-auto text-sm text-caption">Give the user</span>
					<InputText class="w-fit !my-0" type="number" v-model="reEnabledGraceDays" :disabled="isReadOnly" input-width="!w-24" input-height="!h-9.5"
					           :input-filter="(event) => nonEmptyNumberFilter(event, (val) => reEnabledGraceDays = val)"
					           :before-input="(event) => beforeEditNumber(event, (val) => reEnabledGraceDays = val)" />
					<span class="h-fit my-auto text-sm text-caption">{{ FormatUtils.pluralize('day', reEnabledGraceDays) }} to log in before the account is disabled again</span>
				</div>
				<ValidationError :errors="validation.errors" field="settings.inactive_accounts.reenabled_grace_days" class="mt-2 ml-1" />

				<div class="flex flex-col rounded-xl text-sm p-3 bg-primary-subtle mt-5 text-caption font-medium">
					<span>◦ A warning email is sent {{ warningDays }} {{ FormatUtils.pluralize('day', warningDays) }} before an account is disabled.</span>
					<span>◦ Accounts that are inactive for {{ lastLoginDays }} {{ FormatUtils.pluralize('day', lastLoginDays) }} are automatically disabled. The user will receive a notice via email.</span>
					<span>◦ After an account is re-enabled, the user has {{ reEnabledGraceDays }} {{ FormatUtils.pluralize('day', reEnabledGraceDays) }} to log in before the account is disabled again. Because passwords expire, the user must set a new password on next log in.</span>
					<span class="font-semibold">◦ Only an admin can re-enable a disabled account.</span>
				</div>
			</div>
		</div>

		<div class="flex w-full mt-4 space-x-4">
			<SubmitButton label="Save" :size="ButtonSize.Small" @click.prevent="save" :disabled="isSaving" :loading="isSaving" />
			<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="changesMade"
			              class="mt-3 sm:mt-0" :class="{'sm:mr-4': true}" />
		</div>
	</div>

	<Teleport to="html">
		<MultiButtonModal
			v-if="showDiscardChangesModal"
			:show="true"
			icon="info"
			title="Discard Changes"
			message="Are you sure?"
			subMessage="Leaving now will discard any changes made."
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDiscardChangesModal = false }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { showDiscardChangesModal = false; emit('dismiss') }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>
</template>

<style scoped>
</style>